import { UtilService } from '../../shared/services/util.service';
import { LiquidacaoEncargosStruct } from '../../shared/services/structs/financas/liquidacao-encargos.struct';

export interface ConfigEncargos {
    percentualJurosDiario: number;
    percentualMultaMensal: number;
    diasCarenciaMulta: number;
    fonte: 'FormaRecebimento' | 'ContaFinanceira';
}

export interface ContaFinanceiraEncargosConfig {
    idContaFinanceira: number;
    hasJurosMulta: boolean;
    percentualJurosDiario?: number | null;
    percentualMultaMensal?: number | null;
    diasCarenciaMulta?: number | null;
    [key: string]: any;
}

export class EncargosUtil {

    /**
     * Resolve a configuração de encargos ativa para uma parcela.
     * Prioridade: Crediário > Conta Financeira > null.
     */
    static resolverConfiguracao(
        // Dados do Crediário vinculado à parcela (vem do backend preparar)
        hasJurosMultaFormaRecebimento: boolean,
        percentualJurosDiarioFormaRecebimento: number | null,
        percentualMultaMensalFormaRecebimento: number | null,
        diasCarenciaMultaFormaRecebimento: number | null,
        // Dados da ContaFinanceira selecionada no momento
        contaFinanceira: ContaFinanceiraEncargosConfig | null
    ): ConfigEncargos | null {

        // 1. Crediário tem prioridade
        if (hasJurosMultaFormaRecebimento
            && percentualJurosDiarioFormaRecebimento != null
            && percentualMultaMensalFormaRecebimento != null
            && diasCarenciaMultaFormaRecebimento != null) {
            return {
                percentualJurosDiario: percentualJurosDiarioFormaRecebimento,
                percentualMultaMensal: percentualMultaMensalFormaRecebimento,
                diasCarenciaMulta: diasCarenciaMultaFormaRecebimento,
                fonte: 'FormaRecebimento'
            };
        }

        // 2. Conta financeira selecionada
        if (contaFinanceira?.hasJurosMulta
            && contaFinanceira.percentualJurosDiario != null
            && contaFinanceira.percentualMultaMensal != null
            && contaFinanceira.diasCarenciaMulta != null) {
            return {
                percentualJurosDiario: contaFinanceira.percentualJurosDiario,
                percentualMultaMensal: contaFinanceira.percentualMultaMensal,
                diasCarenciaMulta: contaFinanceira.diasCarenciaMulta,
                fonte: 'ContaFinanceira'
            };
        }

        return null;
    }

    /**
     * Calcula juros simples, evitando dupla cobrança no mesmo dia.
     */
    static calcularJuros(
        config: ConfigEncargos,
        valorEmAberto: number,
        vencimento: Date,
        dataRecebimento: Date,
        liquidacoesExistentes: LiquidacaoEncargosStruct[]
    ): number {
        const diasAtraso = Math.max(0, Math.floor(
            (dataRecebimento.getTime() - vencimento.getTime()) / (1000 * 60 * 60 * 24)
        ));

        if (diasAtraso <= 0) return 0;

        // Se já existe liquidação com juros no mesmo dia → não cobrar novamente
        const jaCobrouHoje = liquidacoesExistentes.some(l =>
            l.valorJurosLiquidado > 0 &&
            new Date(l.dataRecebimento).toDateString() === dataRecebimento.toDateString()
        );
        if (jaCobrouHoje) return 0;

        const juros = valorEmAberto * (config.percentualJurosDiario / 100) * diasAtraso;
        return UtilService.arredondarAbnt(juros, 2);
    }

    /**
     * Calcula multa mensal, evitando dupla cobrança no mesmo mês.
     */
    static calcularMulta(
        config: ConfigEncargos,
        valorEmAberto: number,
        vencimento: Date,
        dataRecebimento: Date,
        liquidacoesExistentes: LiquidacaoEncargosStruct[]
    ): number {
        const diasAtraso = Math.max(0, Math.floor(
            (dataRecebimento.getTime() - vencimento.getTime()) / (1000 * 60 * 60 * 24)
        ));

        if (diasAtraso <= config.diasCarenciaMulta) return 0;

        // Se já existe multa cobrada no mesmo mês/ano → não cobrar novamente
        const jaCobrouMes = liquidacoesExistentes.some(l => {
            if (l.valorMultaLiquidado <= 0) return false;
            const dl = new Date(l.dataRecebimento);
            return dl.getFullYear() === dataRecebimento.getFullYear()
                && dl.getMonth() === dataRecebimento.getMonth();
        });
        if (jaCobrouMes) return 0;

        const multa = valorEmAberto * (config.percentualMultaMensal / 100);
        return UtilService.arredondarAbnt(multa, 2);
    }
}
